import { useEffect, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { buttonVariants } from "@cronika/ui";
import { useRegisterModal } from "@/core/providers/RegisterModalContext";
import { registerPartner } from "@/features/register/api";
import { useContent } from "@/shared/lib/i18n";

type State = "idle" | "loading" | "success" | "error";

export function SignUpModal() {
  const { open, closeModal } = useRegisterModal();
  const { signUpModal: t } = useContent();
  const [email, setEmail] = useState("");
  const [telegram, setTelegram] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [state, setState] = useState<State>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, closeModal]);

  function reset() {
    setEmail("");
    setTelegram("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirm(false);
    setState("idle");
    setErrorMsg("");
  }

  function handleClose() {
    closeModal();
    setTimeout(reset, 300);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirmPassword) {
      setState("error");
      setErrorMsg(t.errors.passwordMismatch);
      return;
    }
    setState("loading");
    setErrorMsg("");
    try {
      await registerPartner({ email, password, telegram: telegram || undefined });
      setState("success");
    } catch (err) {
      setState("error");
      setErrorMsg(err instanceof Error ? err.message : t.errors.generic);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[100]"
            style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
          />

          <div className="fixed inset-0 z-[101] flex items-center justify-center px-2 py-4">
            <motion.div
              className="w-full max-w-[740px]"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <div
                className="relative rounded-2xl px-5 py-8 sm:px-10 sm:py-10"
                style={{
                  background: "rgba(140,140,140,0.15)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              >
                {/* Close */}
                <button
                  onClick={handleClose}
                  className="absolute top-5 right-5 flex items-center justify-center w-8 h-8 text-white/50 hover:text-white transition-colors"
                  aria-label={t.closeAriaLabel}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>

                {state === "success" ? (
                  <div className="text-center py-4 sm:py-6">
                    <div
                      className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-5"
                      style={{ background: "rgba(244,164,37,0.15)" }}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path
                          d="M6 16.5L13 23L26 9"
                          stroke="#f4a425"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">{t.success.title}</h2>
                    <p className="text-base text-white/55 leading-relaxed max-w-[380px] mx-auto">
                      {t.success.description}
                    </p>
                    <button
                      onClick={handleClose}
                      className="mt-8 flex items-center justify-center w-full h-12 rounded-xl text-sm font-medium text-white/60 hover:text-white border border-white/10 hover:border-white/20 transition-all"
                    >
                      {t.success.close}
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-6 sm:mb-8">{t.title}</h2>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
                      {/* Row: Email + Telegram */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm mb-2">{t.fields.email.label} <span className="text-red-400">*</span></label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                              <img src="/icons/mail.svg" width={20} height={20} alt="" />
                            </span>
                            <input
                              type="email"
                              required
                              placeholder={t.fields.email.placeholder}
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full h-12 rounded-xl pl-11 pr-4 text-sm text-white placeholder-white/30 outline-none transition-colors"
                              style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                              onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(244,164,37,0.5)")}
                              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm mb-2">{t.fields.telegram.label}</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                              <img src="/icons/telegram.svg" width={20} height={17} alt="" />
                            </span>
                            <input
                              type="text"
                              placeholder={t.fields.telegram.placeholder}
                              value={telegram}
                              onChange={(e) => setTelegram(e.target.value)}
                              className="w-full h-12 rounded-xl pl-11 pr-4 text-sm text-white placeholder-white/30 outline-none transition-colors"
                              style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                              onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(244,164,37,0.5)")}
                              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Password */}
                      <div>
                        <label className="block text-sm mb-2">{t.fields.password.label} <span className="text-red-400">*</span></label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <img src="/icons/lock.svg" width={20} height={20} alt="" />
                          </span>
                          <input
                            type={showPassword ? "text" : "password"}
                            required
                            minLength={8}
                            placeholder={t.fields.password.placeholder}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full h-12 rounded-xl pl-11 pr-12 text-sm text-white placeholder-white/30 outline-none transition-colors"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                            onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(244,164,37,0.5)")}
                            onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-90 transition-opacity"
                            tabIndex={-1}
                          >
                            <img src="/icons/eye.svg" width={20} height={20} alt="" style={{ opacity: showPassword ? 1 : 0.5 }} />
                          </button>
                        </div>
                      </div>

                      {/* Repeat password */}
                      <div>
                        <label className="block text-sm mb-2">{t.fields.confirmPassword.label} <span className="text-red-400">*</span></label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <img src="/icons/lock.svg" width={20} height={20} alt="" />
                          </span>
                          <input
                            type={showConfirm ? "text" : "password"}
                            required
                            placeholder={t.fields.confirmPassword.placeholder}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full h-12 rounded-xl pl-11 pr-12 text-sm text-white placeholder-white/30 outline-none transition-colors"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                            onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(244,164,37,0.5)")}
                            onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirm((v) => !v)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-90 transition-opacity"
                            tabIndex={-1}
                          >
                            <img src="/icons/eye.svg" width={20} height={20} alt="" style={{ opacity: showConfirm ? 1 : 0.5 }} />
                          </button>
                        </div>
                      </div>

                      {state === "error" && (
                        <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                          {errorMsg}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={state === "loading"}
                        className={buttonVariants({ variant: "glow", size: "lg", radius: "xl", className: "w-full mt-1 disabled:opacity-60" })}
                      >
                        {state === "loading" ? t.submitting : t.submit}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
